import React, { useState, useRef, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import Link from 'next/link'
import ContactImage from '@/images/contact-form-photo.jpeg'
import { toast } from 'react-toastify'
import { IoIosArrowDown, IoIosArrowUp } from 'react-icons/io'
import { trackGenerateLead } from '@/utils/dataLayer'
import { getStoredUtmParams } from '@/utils/utm'

const phonePrefixes = [
    { value: '10', title: '010' },
    { value: '50', title: '050' },
    { value: '51', title: '051' },
    { value: '55', title: '055' },
    { value: '60', title: '060' },
    { value: '70', title: '070' },
    { value: '77', title: '077' },
    { value: '99', title: '099' },
]

const formatPhoneNumber = (value = '') => {
    const numbers = value.replace(/\D/g, '').slice(0, 7)

    if (numbers.length <= 3) return numbers
    if (numbers.length <= 5) return `${numbers.slice(0, 3)} ${numbers.slice(3)}`
    return `${numbers.slice(0, 3)} ${numbers.slice(3, 5)} ${numbers.slice(5)}`
}

const ContactForm = ({ onSuccess, showSuccessToast = true, hideImageOnMobile = false }) => {
    const { t } = useTranslation('common')
    const [isLoading, setIsLoading] = useState(false)
    const [numberPrefix, setNumberPrefix] = useState('50')
    const [isSelectOpen, setIsSelectOpen] = useState(false)
    const selectRef = useRef(null)

    const {
        register,
        handleSubmit,
        setValue,
        reset,
        trigger,
        getValues,
        formState: { errors },
    } = useForm({
        mode: 'onSubmit',
        defaultValues: {
            first_name: '',
            last_name: '',
            email: '',
            phone: '',
            consent: true,
        },
    })

    useEffect(() => {
        const handleAutoFill = () => {
            const { consent, ...fields } = getValues()
            const hasValues = Object.values(fields).some(value => value && value.toString().trim() !== '')
            if (hasValues) {
                trigger()
            }
        }

        window.addEventListener('focus', handleAutoFill)
        return () => window.removeEventListener('focus', handleAutoFill)
    }, [trigger, getValues])

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (selectRef.current && !selectRef.current.contains(e.target)) {
                setIsSelectOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const onSubmit = async (data) => {
        setIsLoading(true)

        const utm = getStoredUtmParams()
        const body = {
            full_name: `${data.first_name} ${data.last_name}`.trim(),
            email: data.email,
            phone: `+994${numberPrefix}${(data.phone || '').replace(/\D/g, '')}`,
            source_platform: window.location.pathname,
            legal: !!data.consent,
            utm_source: utm.utm_source ?? null,
            utm_campaign: utm.utm_campaign ?? null,
            utm_content: utm.utm_content ?? null,
            utm_medium: utm.utm_medium ?? null,
            utm_params: utm.utm_params ?? null,
        }

        try {
            const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/leads/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            })

            if (!res.ok) throw new Error()

            trackGenerateLead({
                firstName: data.first_name,
                lastName: data.last_name,
                email: data.email,
                phone: body.phone,
                formLocation: body.source_platform,
                formType: 'student form',
            })

            if (showSuccessToast) {
                toast.success(t('contact-us-page.contact-form.success-message', 'Müraciətiniz uğurla göndərildi!'), {
                    position: 'top-right',
                    autoClose: 5000,
                    theme: 'dark',
                })
            }

            reset()
            setNumberPrefix('50')
            onSuccess?.()
        } catch {
            toast.error(t('contact-us-page.contact-form.error-message', 'Xəta baş verdi. Zəhmət olmasa yenidən cəhd edin.'), {
                position: 'top-right',
                autoClose: 5000,
                theme: 'dark',
            })
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div className="w-full max-w-[90%] mx-auto pb-[80px] pt-[40px] md:py-[100px]">
            <div className="flex flex-col-reverse lg:flex-row gap-[40px] md:gap-[80px] xl:gap-[160px] items-center">
                <div className="flex-1 w-full">
                    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-[24px]">

                        <span className="text-[24px] xs:text-[32px] md:text-[40px] lg:text-[48px] font-[500] leading-[120%] bg-gradient-to-r from-[#091F59] to-[#1A3884] bg-clip-text text-transparent">
                            <span dangerouslySetInnerHTML={{ __html: t('contact-us-page.contact-form.title-part') }} />
                        </span>

                        <div className="flex md:flex-row flex-col justify-between gap-[20px]">
                            <div className="flex flex-col gap-[8px] w-full">
                                <label className="font-inter text-[14px] font-[600] text-[#686868] sm:text-[16px]">
                                    {t('contact-us-page.contact-form.first-name-label')}
                                    <span className="text-main-pink">*</span>
                                </label>
                                <input
                                    className="h-[48px] md:h-[56px] w-full rounded-[10px] px-[16px] md:px-[20px] font-light bg-[#F5F7F9] border-2 border-transparent focus:border-main-pink focus:outline-none transition-all duration-300"
                                    {...register('first_name', { required: 'Required' })}
                                    placeholder={t('contact-us-page.contact-form.first-name-placeholder')}
                                />
                                {errors.first_name && <span className="text-red-500 text-[12px]">{errors.first_name.message}</span>}
                            </div>

                            <div className="flex flex-col gap-[8px] w-full">
                                <label className="font-inter text-[14px] font-[600] text-[#686868] sm:text-[16px]">
                                    {t('contact-us-page.contact-form.last-name-label')}
                                    <span className="text-main-pink">*</span>
                                </label>
                                <input
                                    className="h-[48px] md:h-[56px] w-full rounded-[10px] px-[16px] md:px-[20px] font-light bg-[#F5F7F9] border-2 border-transparent focus:border-main-pink focus:outline-none transition-all duration-300"
                                    {...register('last_name', { required: 'Required' })}
                                    placeholder={t('contact-us-page.contact-form.last-name-placeholder')}
                                />
                                {errors.last_name && <span className="text-red-500 text-[12px]">{errors.last_name.message}</span>}
                            </div>
                        </div>

                        <div className="flex flex-col gap-[8px]">
                            <label className="font-inter text-[14px] font-[600] text-[#686868] sm:text-[16px]">
                                {t('contact-us-page.contact-form.email-label')}
                                <span className="text-main-pink">*</span>
                            </label>
                            <input
                                type="email"
                                className="h-[48px] md:h-[56px] w-full rounded-[10px] px-[16px] md:px-[20px] font-light bg-[#F5F7F9] border-2 border-transparent focus:border-main-pink focus:outline-none transition-all duration-300"
                                {...register('email', {
                                    required: 'Required',
                                    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Invalid email' },
                                })}
                                placeholder={t('contact-us-page.contact-form.email-placeholder')}
                            />
                            {errors.email && <span className="text-red-500 text-[12px]">{errors.email.message}</span>}
                        </div>

                        <div className="flex flex-col gap-[8px]">
                            <label className="font-inter text-[14px] font-[600] text-[#686868] sm:text-[16px]">
                                {t('contact-us-page.contact-form.phone-label')}
                                <span className="text-main-pink">*</span>
                            </label>

                            <div className="flex h-[48px] md:h-[56px]">
                                <div ref={selectRef} className="relative h-full w-[100px]">
                                    <button
                                        type="button"
                                        onClick={() => setIsSelectOpen(!isSelectOpen)}
                                        className={`h-full w-[100px] border-r-[2px] border-[#D9D9D9] px-[10px] bg-[#F5F7F9] flex items-center justify-center gap-[15px] ${isSelectOpen ? 'rounded-tl-[10px]' : 'rounded-l-[10px]'
                                            }`}
                                    >
                                        {phonePrefixes.find(p => p.value === numberPrefix)?.title}
                                        {isSelectOpen ? <IoIosArrowUp /> : <IoIosArrowDown />}
                                    </button>

                                    {isSelectOpen && (
                                        <div className="phone-prefix-dropdown absolute overflow-y-auto max-h-[200px] top-full left-0 w-[100px] bg-[#F5F7F9] rounded-bl-[10px] shadow-lg z-50">
                                            {phonePrefixes.map(p => (
                                                <button
                                                    key={p.value}
                                                    type="button"
                                                    onClick={() => {
                                                        setNumberPrefix(p.value)
                                                        setIsSelectOpen(false)
                                                    }}
                                                    className="w-full h-[56px] hover:bg-[#E8E8E8]"
                                                >
                                                    {p.title}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <input
                                    placeholder="--- -- --"
                                    className="h-full w-full rounded-r-[10px] px-[16px] md:px-[20px] font-light bg-[#F5F7F9] border-2 border-transparent focus:border-main-pink focus:outline-none transition-all duration-300"
                                    {...register('phone', {
                                        required: 'Phone is required',
                                        validate: v => (v?.replace(/\D/g, '').length === 7) || 'Phone number must be 7 digits',
                                        onChange: e => {
                                            const formatted = formatPhoneNumber(e.target.value)
                                            setValue('phone', formatted.replace(/\D/g, ''), { shouldValidate: false })
                                            e.target.value = formatted
                                        },
                                    })}
                                />
                            </div>

                            {errors.phone && <span className="text-red-500 text-[12px]">{errors.phone.message}</span>}
                        </div>

                        <div className="flex flex-col gap-[6px]">
                            <div className="flex items-start gap-[10px]">
                                <input
                                    id="consent"
                                    type="checkbox"
                                    defaultChecked
                                    className="mt-[3px] h-[18px] w-[18px] shrink-0 cursor-pointer accent-main-pink"
                                    {...register('consent', {
                                        required: t('contact-us-page.contact-form.consent.error'),
                                    })}
                                />
                                <label
                                    htmlFor="consent"
                                    className="font-inter cursor-pointer text-[13px] font-[400] leading-[150%] text-[#686868] sm:text-[14px]"
                                >
                                    {t('contact-us-page.contact-form.consent.before')}
                                    <Link href="/privacy-policy" target="_blank" className="text-main-pink hover:underline">
                                        {t('contact-us-page.contact-form.consent.privacy')}
                                    </Link>
                                    {t('contact-us-page.contact-form.consent.middle')}
                                    <Link href="/terms-of-conditions" target="_blank" className="text-main-pink hover:underline">
                                        {t('contact-us-page.contact-form.consent.terms')}
                                    </Link>
                                    {t('contact-us-page.contact-form.consent.after')}
                                </label>
                            </div>
                            {errors.consent && <span className="text-red-500 text-[12px]">{errors.consent.message}</span>}
                        </div>

                        <button
                            type="submit"
                            disabled={isLoading}
                            className="h-[48px] md:h-[56px] w-full rounded-full bg-main-pink text-white hover:bg-white hover:text-main-pink border border-main-pink transition-all duration-300 mt-[8px]"
                        >
                            {isLoading ? 'Göndərilir...' : t('contact-us-page.contact-form.submit-button')}
                        </button>
                    </form>
                </div>

                <div className={`flex-1 items-center justify-center w-full ${hideImageOnMobile ? 'hidden lg:flex' : 'flex'}`}>
                    <div className="relative w-full max-w-[540px] aspect-square">
                        <Image
                            src={ContactImage}
                            alt="Contact"
                            priority
                            fill
                            sizes="(max-width: 1024px) 90vw, 540px"
                            className="rounded-[32px] object-cover"
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ContactForm
